import * as Sentry from "@sentry/react";
import { useSyncExternalStore } from "react";
import type { z } from "zod";
import { dropPersistedQueryCache } from "../services/queryPersistence";

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

type SetStateAction<T> = T | ((prev: T) => T);
type Unsubscribe = () => void;
type Listener<T> = (value: T) => void;

export type Store<T> = {
  get: () => T;
  set: (action: SetStateAction<T>) => void;
  subscribe: (listener: Listener<T>) => Unsubscribe;
};

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function resolveAction<T>(action: SetStateAction<T>, prev: T): T {
  return typeof action === "function"
    ? (action as (prev: T) => T)(prev)
    : action;
}

function createBus<T>(): {
  subscribe: (listener: Listener<T>) => Unsubscribe;
  emit: (value: T) => void;
} {
  let listeners: Listener<T>[] = [];
  return {
    subscribe(listener) {
      listeners.push(listener);
      return () => {
        listeners = listeners.filter((l) => l !== listener);
      };
    },
    emit(value) {
      for (const l of listeners) l(value);
    },
  };
}

// ---------------------------------------------------------------------------
// useStore — React binding
// ---------------------------------------------------------------------------

export function useStore<T>(store: Store<T>): [T, Store<T>["set"]] {
  const value = useSyncExternalStore(store.subscribe, store.get, store.get);
  return [value, store.set];
}

// ---------------------------------------------------------------------------
// Memory store
// ---------------------------------------------------------------------------

export function memoryStore<T>(initialValue: T): Store<T> {
  let state = initialValue;
  const { subscribe, emit } = createBus<T>();
  return {
    get: () => state,
    set(action) {
      const next = resolveAction(action, state);
      if (next === state) return;
      state = next;
      emit(state);
    },
    subscribe,
  };
}

export function createMemoryStore<T>(
  defaultValue: T
): () => [T, Store<T>["set"]] {
  const store = memoryStore(defaultValue);
  return () => useStore(store);
}

// ---------------------------------------------------------------------------
// Session store
// ---------------------------------------------------------------------------

const sessionKeys = new Set<string>();

export function sessionStore<T>(
  key: string,
  defaultValue: T,
  schema: z.ZodType<T>
): Store<T> {
  if (sessionKeys.has(key) && !import.meta.hot) {
    throw new Error(`sessionStore key "${key}" already declared.`);
  }
  sessionKeys.add(key);

  const { subscribe, emit } = createBus<T>();

  function read(): T {
    const raw = globalThis.sessionStorage.getItem(key);
    if (raw === null) return defaultValue;
    try {
      return schema.parse(JSON.parse(raw));
    } catch {
      globalThis.sessionStorage.removeItem(key);
      return defaultValue;
    }
  }

  function write(value: T): void {
    if (value === defaultValue) {
      globalThis.sessionStorage.removeItem(key);
    } else {
      globalThis.sessionStorage.setItem(key, JSON.stringify(value));
    }
  }

  // Cache the last snapshot so useSyncExternalStore sees a stable reference.
  // Without this, read() returns a new object on every call (JSON.parse),
  // which violates useSyncExternalStore's getSnapshot contract and causes
  // infinite re-renders.
  let cache: T = read();
  let cacheJson: string = JSON.stringify(cache);

  function cachedRead(): T {
    const freshJson = globalThis.sessionStorage.getItem(key);
    const normalised =
      freshJson === null ? JSON.stringify(defaultValue) : freshJson;
    if (normalised !== cacheJson) {
      cache = read();
      cacheJson = JSON.stringify(cache);
    }
    return cache;
  }

  return {
    get: cachedRead,
    set(action) {
      const value = resolveAction(action, cachedRead());
      write(value);
      cache = value;
      cacheJson = JSON.stringify(value);
      emit(value);
    },
    subscribe,
  };
}

export function createSessionStore<T>(
  key: string,
  defaultValue: T,
  schema: z.ZodType<T>
): () => [T, Store<T>["set"]] {
  const store = sessionStore(key, defaultValue, schema);
  return () => useStore(store);
}

// ---------------------------------------------------------------------------
// Local store
// ---------------------------------------------------------------------------

const localKeys = new Set<string>();

export function localStore<T>(
  key: string,
  defaultValue: T,
  schema: z.ZodType<T>
): Store<T> {
  if (localKeys.has(key) && !import.meta.hot) {
    throw new Error(`localStore key "${key}" already declared.`);
  }
  localKeys.add(key);

  const { subscribe, emit } = createBus<T>();

  function read(): T {
    const raw = globalThis.localStorage.getItem(key);
    if (raw === null) return defaultValue;
    try {
      return schema.parse(JSON.parse(raw));
    } catch {
      globalThis.localStorage.removeItem(key);
      return defaultValue;
    }
  }

  function write(value: T): void {
    if (value === defaultValue) {
      globalThis.localStorage.removeItem(key);
      return;
    }
    const json = JSON.stringify(value);
    try {
      globalThis.localStorage.setItem(key, json);
    } catch (error: unknown) {
      // A full localStorage: these writes run in render effects, so the page
      // would fall to the error boundary. The persisted query cache gives the
      // room back; a second failure has another cause and throws. Reported so
      // that a cache budget too large, or another occupant, shows up.
      Sentry.captureException(error, {
        tags: { source: "local-store", failure: "quota-recovered" },
      });
      dropPersistedQueryCache();
      globalThis.localStorage.setItem(key, json);
    }
  }

  // Cache the last snapshot so useSyncExternalStore sees a stable reference.
  // Without this, read() returns a new object on every call (JSON.parse),
  // which violates useSyncExternalStore's getSnapshot contract and causes
  // infinite re-renders.
  let cache: T = read();
  let cacheJson: string = JSON.stringify(cache);

  function cachedRead(): T {
    const freshJson = globalThis.localStorage.getItem(key);
    const normalised =
      freshJson === null ? JSON.stringify(defaultValue) : freshJson;
    if (normalised !== cacheJson) {
      cache = read();
      cacheJson = JSON.stringify(cache);
    }
    return cache;
  }

  return {
    get: cachedRead,
    set(action) {
      const value = resolveAction(action, cachedRead());
      write(value);
      cache = value;
      cacheJson = JSON.stringify(value);
      emit(value);
    },
    subscribe,
  };
}

export function createLocalStore<T>(
  key: string,
  defaultValue: T,
  schema: z.ZodType<T>
): () => [T, Store<T>["set"]] {
  const store = localStore(key, defaultValue, schema);
  return () => useStore(store);
}
